/**
 * LegalPeople Resource
 * Manages legal entities (pessoas jurídicas) scoped by company
 *
 * ## Restrição de formato do `company_id`
 *
 * Estas rotas aceitam **somente** `company_id` no formato `ObjectId` de 24
 * hexadecimais. Empresa cujo id tem 32 caracteres recebe
 * `400 "company id is not valid"` em toda chamada — a rota valida o id como
 * `ObjectId` antes de qualquer coisa.
 *
 * É **limite do servidor**, não do SDK: não há conversão possível entre os dois
 * formatos, e validar localmente só antecipa a mesma recusa com mensagem pior.
 * Medido em 2026-09-02 sobre 50 empresas da mesma conta:
 *
 *   30 empresas com id de 24 hex   -> 200
 *   19 empresas com id de 32 chars -> 400 "company id is not valid"
 *
 * Um id de 24 hex sintético (inexistente) responde `404 "Company not found."`,
 * ou seja: o validador de formato passa e a busca é que falha. Empresas criadas
 * depois da mudança de formato de id ficaram inalcançáveis por estas rotas.
 * Pendência aberta com o time de API.
 */

import type { HttpClient } from '../http/client.js';
import type { LegalPerson, ResourceId, ListResponse } from '../types.js';

/**
 * LegalPeople resource for managing legal entities (pessoas jurídicas)
 * All operations are scoped by company_id
 */
export class LegalPeopleResource {
  constructor(private readonly http: HttpClient) {}

  /**
   * List all legal people for a company
   *
   * @param companyId - Company ID
   * @returns List of legal people
   *
   * @example
   * ```typescript
   * const result = await nfe.legalPeople.list('company-id');
   * console.log(`Found ${result.legalPeople?.length ?? 0} legal entities`);
   * ```
   */
  async list(companyId: ResourceId): Promise<ListResponse<LegalPerson>> {
    const path = `/companies/${companyId}/legalpeople`;
    const response = await this.http.get<{ legalPeople: LegalPerson[] }>(path);

    // API returns: { legalPeople: [...] }
    // Transform to our standard ListResponse format
    return {
      data: response.data.legalPeople || []
    };
  }

  /**
   * Create a new legal person
   *
   * @param companyId - Company ID
   * @param data - Legal person data
   * @returns Created legal person
   *
   * @example
   * ```typescript
   * const legalPerson = await nfe.legalPeople.create('company-id', {
   *   federalTaxNumber: '12345678901234',
   *   name: 'Empresa Exemplo Ltda',
   *   email: 'contato@empresa.com.br',
   *   address: {
   *     street: 'Av. Paulista, 1000',
   *     neighborhood: 'Bela Vista',
   *     city: { code: '3550308', name: 'São Paulo' },
   *     state: 'SP',
   *     postalCode: '01310-100'
   *     }
   * });
   * ```
   */
  async create(
    companyId: ResourceId,
    data: Partial<LegalPerson>
  ): Promise<LegalPerson> {
    const path = `/companies/${companyId}/legalpeople`;
    const response = await this.http.post<{ legalPeople: LegalPerson }>(path, data);

    // API returns wrapped object: { legalPeople: {...} }
    return response.data.legalPeople;
  }

  /**
   * Retrieve a specific legal person
   *
   * @param companyId - Company ID
   * @param legalPersonId - Legal person ID
   * @returns Legal person details
   *
   * @example
   * ```typescript
   * const legalPerson = await nfe.legalPeople.retrieve(
   *   'company-id',
   *   'legal-person-id'
   * );
   * console.log(legalPerson.name);
   * ```
   */
  async retrieve(
    companyId: ResourceId,
    legalPersonId: ResourceId
  ): Promise<LegalPerson> {
    const path = `/companies/${companyId}/legalpeople/${legalPersonId}`;
    const response = await this.http.get<{ legalPeople: LegalPerson }>(path);

    // API returns wrapped object: { legalPeople: {...} }
    return response.data.legalPeople;
  }

  /**
   * Update a legal person
   *
   * @param companyId - Company ID
   * @param legalPersonId - Legal person ID
   * @param data - Data to update
   * @returns Updated legal person
   *
   * @example
   * ```typescript
   * const updated = await nfe.legalPeople.update(
   *   'company-id',
   *   'legal-person-id',
   *   { email: 'novo@email.com' }
   * );
   * ```
   */
  async update(
    companyId: ResourceId,
    legalPersonId: ResourceId,
    data: Partial<LegalPerson>
  ): Promise<LegalPerson> {
    const path = `/companies/${companyId}/legalpeople/${legalPersonId}`;
    const response = await this.http.put<{ legalPeople: LegalPerson }>(path, data);

    // API returns wrapped object: { legalPeople: {...} }
    return response.data.legalPeople;
  }

  /**
   * Delete a legal person
   *
   * @param companyId - Company ID
   * @param legalPersonId - Legal person ID
   *
   * @example
   * ```typescript
   * await nfe.legalPeople.delete('company-id', 'legal-person-id');
   * ```
   */
  async delete(
    companyId: ResourceId,
    legalPersonId: ResourceId
  ): Promise<void> {
    const path = `/companies/${companyId}/legalpeople/${legalPersonId}`;
    await this.http.delete(path);
  }

  /**
   * Create multiple legal people in batch
   *
   * @param companyId - Company ID
   * @param data - Array of legal people data
   * @returns Array of created legal people
   *
   * @example
   * ```typescript
   * const created = await nfe.legalPeople.createBatch('company-id', [
   *   { name: 'Empresa 1', federalTaxNumber: '11111111111111', ... },
   *   { name: 'Empresa 2', federalTaxNumber: '22222222222222', ... }
   * ]);
   * ```
   */
  async createBatch(
    companyId: ResourceId,
    data: Array<Partial<LegalPerson>>
  ): Promise<LegalPerson[]> {
    const promises = data.map(person => this.create(companyId, person));
    return Promise.all(promises);
  }

  /**
   * Find legal person by federal tax number (CNPJ)
   *
   * @param companyId - Company ID
   * @param federalTaxNumber - CNPJ (only numbers)
   * @returns Legal person or undefined if not found
   *
   * @example
   * ```typescript
   * const person = await nfe.legalPeople.findByTaxNumber(
   *   'company-id',
   *   '12345678901234'
   * );
   * if (person) {
   *   console.log('Found:', person.name);
   * }
   * ```
   */
  async findByTaxNumber(
    companyId: ResourceId,
    federalTaxNumber: string
  ): Promise<LegalPerson | undefined> {
    const result = await this.list(companyId);
    const people = (result.data ?? []) as LegalPerson[];

    return people.find(
      (person: LegalPerson) =>
        person.federalTaxNumber?.toString() === federalTaxNumber
    );
  }
}
